import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DashboardDataContext from '../../helpers/contexts/dashboardDataContext';
import TickerContext from '../../helpers/contexts/tickerContext';
import { appEventsMessaging } from '../../helpers/hooks/pubSub';
import useFetchDashboardData from '../../helpers/hooks/useFetchDashboardData';
import useFetchTickerData from '../../helpers/hooks/useFetchTickerData';
import usePresenceHeartbeat from '../../helpers/hooks/usePresenceHeartbeat';
import { createCompanyDateSelectionPresets, isSelectedDateRangeToday } from '../../helpers/utils';
import type { RootState } from '../../state/store';
import { DashboardEventsContext } from '../dashboardEvents';
import { filterTickerDataFromWidgets, transformToWidgetsList } from './utils';

interface DashboardDataProviderProps {
  children: React.ReactNode;
  dashboard: string;
}

const DashboardDataProvider = ({ children, dashboard }: DashboardDataProviderProps) => {
  const [dashboardWidgets, setDashboardWidgets] = useState<any[]>([]);
  const tickerContext = useContext(TickerContext);
  if (!tickerContext) {
    throw new Error('DashboardDataProvider must be used within a TickerProvider');
  }
  const { addToTickerContext, addFromTickerQuery, setTickerLoading, resetTickerState } =
    tickerContext;
  const { setDashboard, setStartDate, setStartTime, updateBusinessStartDateTime } =
    useContext(DashboardEventsContext);
  const [enableDashboardFetch, setEnableDashboardFetch] = useState(false);
  const dashboards = useSelector((state: RootState) => state.dashboard.dashboards);

  const dashboardsList = dashboards.map((d) => ({
    display_name: d.label,
    name: d?.api_path,
    identifier: d?.identifier,
    route: `/${d?.api_path}`,
    api_endpoint: `/${d?.api_path}/dashboard`
  }));

  const routeMatch = useParams();
  const shouldFetchBase = 'dashboard' in routeMatch && enableDashboardFetch;

  const { filters, company } = useSelector((state: RootState) => state);
  const shouldFetchDashboardData = shouldFetchBase && (filters.urlDateValidated ?? false);

  // Fetch dashboard data
  const {
    data,
    isLoading: isDashboardDataLoading,
    error: dashboardDataLoadError,
    retry,
    dashboardLockedStatus
  } = useFetchDashboardData({
    dashboard,
    filters,
    shouldFetch: shouldFetchDashboardData
  });

  // Fetch ticker data independently (parallel with dashboard)
  const {
    tickerData: independentTickerData,
    isLoading: isTickerLoading,
    hasTickerQuery,
    reload: reloadTickers
  } = useFetchTickerData({
    dashboard,
    filters,
    shouldFetch: shouldFetchBase
  });

  const dashboardRefreshEvent = useRef<{
    dashboard: string;
    subscription: { subscribe(): void; unsubscribe(): void };
  } | null>(null);

  const isSeletedDateRangeToday = useMemo(() => {
    return isSelectedDateRangeToday(
      filters.dates as [string, string],
      (company?.company_details as any)?.business_end_time || 0,
      company?.company_details?.time_zone_offset || '+00:00'
    );
  }, [
    filters.dates,
    (company?.company_details as any)?.business_end_time,
    company?.company_details?.time_zone_offset
  ]);

  usePresenceHeartbeat(dashboard, isSeletedDateRangeToday);

  useEffect(() => {
    if (!dashboard) return;

    // Clean up previous subscription if dashboard changed
    if (
      dashboardRefreshEvent.current?.dashboard &&
      dashboard !== dashboardRefreshEvent.current?.dashboard
    ) {
      dashboardRefreshEvent.current.subscription.unsubscribe();
      dashboardRefreshEvent.current = null;
    }

    // Only subscribe to real-time updates if viewing "Today"
    if (isSeletedDateRangeToday) {
      dashboardRefreshEvent.current = {
        dashboard,
        subscription: appEventsMessaging(`dashboard_updated_${dashboard}`, {
          onSubscribe() {
            retry();
            if (hasTickerQuery) {
              reloadTickers();
            }
          }
        })
      };
      dashboardRefreshEvent.current.subscription.subscribe();
    } else {
      dashboardRefreshEvent.current?.subscription.unsubscribe();
    }

    return () => {
      dashboardRefreshEvent.current?.subscription.unsubscribe();
    };
  }, [dashboard, isSeletedDateRangeToday, retry, hasTickerQuery, reloadTickers]);

  const dashboardInfo = useMemo(() => {
    const dashboardData = dashboardsList.find((d) => d.name === dashboard);
    setEnableDashboardFetch(true);
    if (dashboardData) {
      return dashboardData;
    }
    return null;
  }, [dashboard]);

  // Reset ticker state when dashboard changes
  useEffect(() => {
    resetTickerState();
  }, [dashboard, resetTickerState]);

  // Update loading state for independent ticker query
  useEffect(() => {
    if (hasTickerQuery) {
      setTickerLoading(isTickerLoading);
    }
  }, [hasTickerQuery, isTickerLoading, setTickerLoading]);

  // Handle independent ticker data
  useEffect(() => {
    if (hasTickerQuery && independentTickerData && !isTickerLoading) {
      addFromTickerQuery(independentTickerData);
    }
  }, [hasTickerQuery, independentTickerData, isTickerLoading, addFromTickerQuery]);

  // Handle dashboard data and legacy ticker data
  useEffect(() => {
    if (data) {
      const { tickerData, updatedWidgetsList } = filterTickerDataFromWidgets(
        transformToWidgetsList(data)
      );
      const happeningsWidget = {
        widget: {
          type: 'HAPPENINGS_EVENT_STREAM',
          title: "What's Happening",
          data: {}
        },
        is_timeless_widget: false,
        widget_visibility: 'visible',
        url: 'happenings-events-viewer',
        widgets: [],
        group: {},
        is_report_enabled: false,
        is_comparison_enabled: false,
        widget_name: 'scheduled_orders'
      };
      setDashboardWidgets([happeningsWidget, ...updatedWidgetsList]);

      // Only use dashboard tickers if NOT using independent ticker query
      if (!hasTickerQuery) {
        addToTickerContext(tickerData);
      }
    }
  }, [data, hasTickerQuery, addToTickerContext]);

  useEffect(() => {
    if (isDashboardDataLoading) {
      setDashboardWidgets([]);
      // Only reset tickers from dashboard if NOT using independent query
      if (!hasTickerQuery) {
        addToTickerContext([]);
      }
    }
  }, [isDashboardDataLoading, hasTickerQuery, addToTickerContext]);

  useEffect(() => {
    if (dashboardInfo && filters.dates[0] && filters.locations.length) {
      const { today: todayPreset } = createCompanyDateSelectionPresets({
        businessEndTimeInSecs: (company.company_details as any)?.business_end_time || 0,
        timezoneOffset: company.company_details?.time_zone_offset || '+00:00'
      });
      updateBusinessStartDateTime(todayPreset.date_range[0], todayPreset.time_range[0]);
      setDashboard(dashboardInfo.name);
      setStartDate(filters.dates[0]);
      setStartTime(filters.timeRange[0]);
    }
  }, [dashboardInfo, filters.dates[0], filters.timeRange[0], filters.locations.length]);

  const refreshDashboardData = () => {
    retry(undefined, { revalidate: true });
    // Also refresh tickers if using independent query
    if (hasTickerQuery) {
      reloadTickers();
    }
  };

  const shouldEnableDashboardFetch = (shouldFetch = true) => {
    setEnableDashboardFetch(shouldFetch);
  };

  return (
    <DashboardDataContext.Provider
      value={{
        dashboardWidgets,
        dashboardInfo,
        isDashboardDataLoading,
        dashboardDataLoadError,
        refreshDashboardData,
        shouldEnableDashboardFetch,
        dashboardLockedStatus
      }}
    >
      {children}
    </DashboardDataContext.Provider>
  );
};

export default DashboardDataProvider;
